import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { SettingsShell } from "@/components/SettingsShell";
import { useBranding } from "@/lib/branding";
import { Field } from "@/components/Field";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "@/components/Toast";
import { AnnouncementAdmin } from "@/components/AnnouncementAdmin";
import { TeamsAdmin } from "@/components/TeamsAdmin";

/**
 * /settings/workspace — mirrors the WORKSPACE panel in designs/Settings.html.
 * Layout: panel > panel-head + panel-body (logo .opt, .frow name/website,
 * plan .opt with pill) + save-row. Admin-only: non-admins see EmptyState.
 */
function WorkspaceSettings() {
  const qc = useQueryClient();
  const { logoUrl } = useBranding();
  const me = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const ws = useQuery({
    queryKey: queryKeys.workspace(),
    queryFn: api.workspace,
  });
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [effortUnit, setEffortUnit] = useState<"points" | "hours">("points");
  // Hydrate from server data exactly once. Earlier the effect re-ran on
  // every `ws.data` identity change, which wiped any in-flight edits when
  // the workspace query refetched in the background.
  const isHydrated = useRef(false);

  useEffect(() => {
    if (isHydrated.current || !ws.data) return;
    setName(ws.data.name);
    setWebsite(ws.data.website);
    setEffortUnit(ws.data.effortUnit ?? "points");
    isHydrated.current = true;
  }, [ws.data]);

  const save = useMutation({
    mutationFn: () => api.updateWorkspace({ name, website, effortUnit }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      // The sidebar brand reads the workspace name via /api/branding; refresh
      // it so a rename shows immediately instead of after the 5-min staleTime.
      qc.invalidateQueries({ queryKey: queryKeys.branding() });
    },
  });

  const isAdmin = me.data?.user.role === "admin";

  if (!isAdmin) {
    return (
      <SettingsShell>
        <div className="panel">
          <div className="panel-body">
            <EmptyState
              icon="settings"
              title="Admin only"
              caption="Workspace settings are available to admins."
            />
          </div>
        </div>
      </SettingsShell>
    );
  }

  const plan = ws.data?.plan ?? "starter";
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);

  // Admin-only logo upload. Server re-checks me.role === 'admin' on every
  // request, so the client gate is purely a UX nicety — it still has to
  // survive a stale me cache without leaking a 403 to the user.
  async function onPickLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await api.uploadWorkspaceLogo(file);
      toast.success("Workspace logo updated.");
      await qc.invalidateQueries({ queryKey: queryKeys.workspace() });
      // /api/branding now serves the logo URL the sidebar renders — refresh it
      // so the new logo appears top-left immediately (T: logo upload didn't
      // reflect because branding was never invalidated).
      await qc.invalidateQueries({ queryKey: queryKeys.branding() });
    } catch (err) {
      toast.error((err as Error).message ?? "Logo upload failed.");
    } finally {
      e.target.value = "";
    }
  }

  return (
    <SettingsShell>
      <div className="panel">
        <div className="panel-head">
          <h3>Workspace</h3>
          <p>Settings for your organisation.</p>
        </div>
        <div className="panel-body">
          {ws.isLoading ? (
            <Skeleton count={3} height={48} />
          ) : (
            <>
              <div className="opt" style={{ paddingTop: 0 }}>
                <div
                  className="avatar avatar-lg"
                  style={{
                    background: "var(--navy-900)",
                    borderRadius: 14,
                    color: "#fff",
                    overflow: "hidden",
                  }}
                >
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Workspace logo"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    (name?.[0]?.toUpperCase() ?? "W")
                  )}
                </div>
                <div className="ot">
                  <div className="otit">Workspace logo</div>
                  <div className="odesc">Upload a PNG or JPG (max 5 MB).</div>
                </div>
                <label className="btn-secondary" style={{ cursor: "pointer" }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    hidden
                    onChange={onPickLogo}
                  />
                  Change
                </label>
              </div>

              <div className="frow" style={{ marginTop: 16 }}>
                <Field id="ws-name" label="Workspace name">
                  <input
                    id="ws-name"
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </Field>
                <Field id="ws-site" label="Website">
                  <input
                    id="ws-site"
                    className="input"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                  />
                </Field>
              </div>

              <div className="frow" style={{ marginTop: 16 }}>
                <Field id="ws-effort-unit" label="Effort estimate unit">
                  <select
                    id="ws-effort-unit"
                    className="input"
                    value={effortUnit}
                    onChange={(e) =>
                      setEffortUnit(e.target.value as "points" | "hours")
                    }
                  >
                    <option value="points">Story points</option>
                    <option value="hours">Hours</option>
                  </select>
                </Field>
              </div>

              <TeamsAdmin />

              <AnnouncementAdmin />

              <div className="opt">
                <div className="ot">
                  <div className="otit">Plan</div>
                  <div className="odesc">Current subscription tier.</div>
                </div>
                <span className="pill pill-done">
                  <span className="dot" />
                  {planLabel}
                </span>
              </div>
            </>
          )}
        </div>
        {!ws.isLoading && (
          <div className="save-row">
            <Button
              variant="primary"
              onClick={() => save.mutate()}
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        )}
      </div>
    </SettingsShell>
  );
}

export const Route = createFileRoute("/settings/workspace")({
  component: WorkspaceSettings,
});
